import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Empty, Modal, Input, Spin, Table, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import InvoiceEditApprovalCardV2 from './InvoiceEditApprovalCardV2';
import {
	searchInvoiceEditApprovalsV2,
	acceptVendorBillChanges,
	rejectVendorBillChanges,
} from 'store/approvalSliceV2';

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const TabsContainer = styled(Box)`
	margin-top: 24px;
`;

const StyledTabs = styled(Tabs)`
	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active {
		.ant-tabs-tab-btn {
			color: #1890ff;
			font-weight: 600;
		}
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const ContentContainer = styled(Box)`
	margin-top: 24px;
`;

const EmptyStateContainer = styled(Box)`
	padding: 60px 20px;
	text-align: center;
`;

const LoadingContainer = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 60px 20px;
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const ApproveModalContent = styled(Box)`
	text-align: center;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	display: block;
	margin-bottom: 16px;
`;

const ModalMessage = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
	line-height: 1.5;
`;

const ModalButtonGroup = styled(Box)`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	border-color: #1890ff;
	color: #1890ff;
	background: #fff;
	min-width: 100px;

	&:hover {
		border-color: #40a9ff;
		color: #40a9ff;
		background: #fff;
	}
`;

const ApproveButtonStyled = styled(Button)`
	background: #1890ff;
	border-color: #1890ff;
	color: #fff;
	min-width: 100px;

	&:hover {
		background: #40a9ff;
		border-color: #40a9ff;
	}
`;

const { TextArea } = Input;

export default function InvoiceEditApprovalPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [activeTab, setActiveTab] = useState('pending');
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApproval, setSelectedApproval] = useState(null);
	const [rejectionReason, setRejectionReason] = useState('');

	const { approvals, loading, actionLoading } = useSelector((state) => state.approvalStateV2);

	const fetchApprovals = useCallback(
		async (status) => {
			// Map UI status to API status: approved -> accepted
			const apiStatus = status === 'approved' ? 'accepted' : status;

			// Use v2-specific search function for new UI
			await dispatch(
				searchInvoiceEditApprovalsV2({
					status: apiStatus,
					limit: 20,
					offset: 0,
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchApprovals(activeTab);
	}, [activeTab, fetchApprovals]);

	// Refresh when returning from details page
	useEffect(() => {
		if (location.state?.refresh) {
			fetchApprovals(activeTab);
			// Clear the refresh flag
			navigate(location.pathname, { replace: true, state: {} });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const handleTabChange = (key) => {
		setActiveTab(key);
	};

	const handleView = (entityId, auditId) => {
		// Navigate to approval details page with entity_id in URL and audit_id in state
		if (auditId && entityId) {
			navigate(`/approvals/invoice-edit-approval/${entityId}`, {
				state: { fromApproval: true, auditId, entityId },
			});
		} else if (entityId) {
			// Fallback to vendor invoice route if no audit ID
			navigate(`/vendor-invoice/${entityId}`, {
				state: { fromApproval: true, auditId },
			});
		}
	};

	const handleApproveClick = (approval) => {
		setSelectedApproval(approval);
		setApproveModalVisible(true);
	};

	const handleRejectClick = (approval) => {
		setSelectedApproval(approval);
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!selectedApproval) return;

		// Use audit_id from the approval object
		const auditId = selectedApproval.audit_id || selectedApproval.id;
		if (!auditId) {
			message.error('Audit ID is missing');
			return;
		}

		const result = await dispatch(
			acceptVendorBillChanges({
				audit_id: auditId,
			})
		);
		if (result.success) {
			setApproveModalVisible(false);
			setSelectedApproval(null);
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const handleRejectConfirm = async () => {
		if (!selectedApproval) {
			return;
		}

		// Use audit_id from the approval object
		const auditId = selectedApproval.audit_id || selectedApproval.id;
		if (!auditId) {
			message.error('Audit ID is missing');
			return;
		}

		const result = await dispatch(
			rejectVendorBillChanges({
				audit_id: auditId,
				reason: rejectionReason || undefined,
			})
		);
		if (result.success) {
			setRejectModalVisible(false);
			setSelectedApproval(null);
			setRejectionReason('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('DD-MM-YYYY');
	};

	const formatTime = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('HH:mm');
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === '') return '-';
		return `₹ ${Number(value).toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	};

	const renderDateTime = (dateString) => {
		if (!dateString) return '-';
		return (
			<>
				<div>{formatDate(dateString)}</div>
				<div style={{ fontSize: '12px', color: '#666' }}>{formatTime(dateString)}</div>
			</>
		);
	};

	const getApprovedColumns = () => [
		{
			title: 'Approved at',
			key: 'approved_at',
			width: 180,
			render: (_, record) => renderDateTime(record.approved_at),
		},
		{
			title: 'Approved by',
			dataIndex: 'approved_by',
			key: 'approved_by',
			width: 150,
		},
	];

	const getRejectedColumns = () => [
		{
			title: 'Rejected at',
			key: 'rejected_at',
			width: 180,
			render: (_, record) => renderDateTime(record.rejected_at || record.approved_at),
		},
		{
			title: 'Rejected by',
			dataIndex: 'rejected_by',
			key: 'rejected_by',
			width: 150,
			render: (_, record) => record.rejected_by || '-',
		},
	];

	const getActionColumn = () => ({
		title: 'Action',
		key: 'action',
		width: 100,
		fixed: 'right',
		render: (_, record) => (
			<Button
				type="text"
				icon={<EyeOutlined />}
				onClick={() => handleView(record.vendor_bill_id || record.id, record.audit_id)}
				title="View Details"
			/>
		),
	});

	const getTableColumns = (tabKey) => {
		const baseColumns = [
			{
				title: 'Invoice No',
				dataIndex: 'invoice_no',
				key: 'invoice_no',
				width: 150,
			},
			{
				title: 'Project Name',
				dataIndex: 'project_name',
				key: 'project_name',
				width: 180,
			},
			{
				title: 'Vendor Name',
				dataIndex: 'vendor_name',
				key: 'vendor_name',
				width: 180,
			},
			{
				title: 'Initial Invoice Value',
				dataIndex: 'initial_invoice_value',
				key: 'initial_invoice_value',
				width: 180,
				render: (value) => formatCurrency(value),
			},
			{
				title: 'Updated Invoice Value',
				dataIndex: 'updated_invoice_value',
				key: 'updated_invoice_value',
				width: 180,
				render: (value) => formatCurrency(value),
			},
			{
				title: 'Billing Period',
				dataIndex: 'billing_period',
				key: 'billing_period',
				width: 150,
			},
			{
				title: 'Created at',
				key: 'created_at',
				width: 180,
				render: (_, record) => renderDateTime(record.created_at),
			},
		];

		// Add tab-specific columns
		if (tabKey === 'approved') {
			baseColumns.push(...getApprovedColumns());
		} else if (tabKey === 'rejected') {
			baseColumns.push(...getRejectedColumns());
		}

		// Add action column (eye icon) to all tabs
		baseColumns.push(getActionColumn());

		return baseColumns;
	};

	const mapApprovalToTableData = (approval) => {
		// Extract data from entity_summary or approval object
		const entitySummary = approval.entity_summary || {};

		return {
			key: approval.id,
			id: approval.entity_id || approval.id,
			vendor_bill_id: approval.entity_id || approval.id,
			audit_id: approval.audit_id || approval.id, // Store audit_id for actions
			// Fields from entity_summary or approval
			invoice_no: entitySummary.vendor_bill_no || approval.vendor_bill_no || '-',
			project_name: entitySummary.project_name || approval.project_name || '-',
			vendor_name: entitySummary.vendor_name || approval.vendor_name || '-',
			initial_invoice_value:
				entitySummary.initial_invoice_value || approval.initial_invoice_value || 0,
			updated_invoice_value:
				entitySummary.updated_invoice_value || approval.updated_invoice_value || 0,
			billing_period: entitySummary.billing_period || approval.billing_period || '-',
			created_at: approval.created_at || approval.requested_at || null,
			// Fields from approval (backend sends these)
			approved_at: approval.updated_at || null,
			approved_by: approval.updated_by_username || '-',
			rejected_at: approval.updated_at || null,
			rejected_by: approval.updated_by_username || '-',
		};
	};

	const renderTableContent = (tabKey) => {
		const tableData = approvals.map(mapApprovalToTableData);
		const columns = getTableColumns(tabKey);

		return (
			<ContentContainer>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} items`,
						position: ['bottomRight'],
					}}
					scroll={{ x: 'max-content' }}
					locale={{
						emptyText: (
							<Empty
								description={
									<Text fontSize="14px" color="#999">
										No {tabKey} invoice edit approvals found
									</Text>
								}
							/>
						),
					}}
				/>
			</ContentContainer>
		);
	};

	const mapApprovalToInvoice = (approval) => {
		const entitySummary = approval.entity_summary || {};
		return {
			id: approval.entity_id || approval.id,
			audit_id: approval.audit_id || approval.id,
			invoice_no: entitySummary.vendor_bill_no || approval.vendor_bill_no || '-',
			project_name: entitySummary.project_name || approval.project_name || '-',
			vendor_name: entitySummary.vendor_name || approval.vendor_name || '-',
			initial_invoice_value:
				entitySummary.initial_invoice_value || approval.initial_invoice_value || 0,
			updated_invoice_value:
				entitySummary.updated_invoice_value || approval.updated_invoice_value || 0,
			billing_period: entitySummary.billing_period || approval.billing_period || '-',
			created_at: approval.created_at || approval.requested_at || null,
		};
	};

	const renderPendingCards = () => (
		<ContentContainer>
			{approvals.map((approval) => {
				const invoice = mapApprovalToInvoice(approval);
				return (
					<InvoiceEditApprovalCardV2
						key={approval.id}
						invoice={invoice}
						onView={() =>
							handleView(
								approval.entity_id || approval.id,
								approval.audit_id || approval.id
							)
						}
						onApprove={() => handleApproveClick(approval)}
						onReject={() => handleRejectClick(approval)}
					/>
				);
			})}
		</ContentContainer>
	);

	const renderEmptyState = (tabKey) => (
		<EmptyStateContainer>
			<Empty
				description={
					<Text fontSize="14px" color="#999">
						No {tabKey} invoice edit approvals found
					</Text>
				}
			/>
		</EmptyStateContainer>
	);

	const renderContent = (tabKey) => {
		if (loading) {
			return (
				<LoadingContainer>
					<Spin size="large" tip="Loading approvals..." />
				</LoadingContainer>
			);
		}

		if (!approvals || approvals.length === 0) {
			return renderEmptyState(tabKey);
		}

		// Show table for approved/rejected tabs, cards for pending
		if (tabKey === 'approved' || tabKey === 'rejected') {
			return renderTableContent(tabKey);
		}

		// Show cards for pending tab
		return renderPendingCards();
	};

	const tabItems = [
		{
			key: 'pending',
			label: 'Pending',
			children: renderContent('pending'),
		},
		{
			key: 'approved',
			label: 'Approved',
			children: renderContent('approved'),
		},
		{
			key: 'rejected',
			label: 'Rejected',
			children: renderContent('rejected'),
		},
	];

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Invoice Edit Approvals</PageTitle>
				<PageSubtitle>Approve or reject vendor bill change requests</PageSubtitle>
			</HeaderContainer>

			<TabsContainer>
				<StyledTabs
					activeKey={activeTab}
					onChange={handleTabChange}
					items={tabItems}
					type="line"
				/>
			</TabsContainer>

			{/* Approve Modal */}
			<Modal
				title={null}
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setSelectedApproval(null);
				}}
				footer={null}
				centered
				width={400}
			>
				<ApproveModalContent>
					<ModalTitle>Are you sure?</ModalTitle>
					<ModalMessage>
						This will accept all pending changes to the vendor bill and recalculate the
						total payable amount. You won&apos;t be able to revert this change.
					</ModalMessage>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setApproveModalVisible(false);
								setSelectedApproval(null);
							}}
							disabled={actionLoading}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							type="primary"
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={actionLoading}
						>
							Accept Changes
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Invoice Edit Changes"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setSelectedApproval(null);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading,
					danger: true,
				}}
			>
				<Box mb={3}>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject these invoice edit changes? The vendor bill
						will remain unchanged.
					</Text>
					{selectedApproval && (
						<Box mt={2} p={2} background="#f5f5f5" borderRadius="4px">
							<Text fontSize="12px" color="#666" display="block">
								Invoice No:{' '}
								{selectedApproval.entity_summary?.vendor_bill_no ||
									selectedApproval.vendor_bill_no ||
									'-'}
							</Text>
							<Text fontSize="12px" color="#666" display="block">
								Edit ID: {selectedApproval.audit_id || selectedApproval.id || '-'}
							</Text>
						</Box>
					)}
				</Box>
				<Box>
					<Text fontSize="14px" color="#333" mb={1} display="block">
						Rejection Reason (Optional)
					</Text>
					<TextArea
						rows={4}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Enter reason for rejection (optional)..."
						maxLength={1000}
						showCount
					/>
				</Box>
			</Modal>
		</PageContainer>
	);
}
